/**
 * React Query hooks for the BusinessProfileRegistry admin UI.
 *
 * These are consumed by `pages/admin/*` only; regular business users
 * do NOT call these endpoints (platform-admin role gate applies upstream).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../shared/hooks/useAuth';
import {
  businessProfilesKeys,
  deployProfile,
  getProfile,
  listDeployments,
  listProfiles,
  updateProfile,
} from './profiles-api';
import type { BusinessProfile, DeployFormValues } from './types';

export function useBusinessProfiles() {
  return useQuery({
    queryKey: businessProfilesKeys.list(),
    queryFn: listProfiles,
    staleTime: 30_000,
  });
}

export function useBusinessProfile(subVerticalKey: string | undefined) {
  return useQuery({
    queryKey: businessProfilesKeys.detail(subVerticalKey ?? '__none__'),
    queryFn: () => {
      if (!subVerticalKey) throw new Error('subVerticalKey is required');
      return getProfile(subVerticalKey);
    },
    enabled: Boolean(subVerticalKey),
    staleTime: 30_000,
  });
}

export function useProfileDeployments(subVerticalKey: string | undefined) {
  return useQuery({
    queryKey: businessProfilesKeys.deployments(subVerticalKey ?? '__none__'),
    queryFn: () => {
      if (!subVerticalKey) throw new Error('subVerticalKey is required');
      return listDeployments(subVerticalKey);
    },
    enabled: Boolean(subVerticalKey),
    staleTime: 15_000,
  });
}

export function useUpdateBusinessProfile(subVerticalKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<BusinessProfile>) =>
      updateProfile(subVerticalKey, patch),
    onSuccess: (next) => {
      queryClient.setQueryData(businessProfilesKeys.detail(subVerticalKey), next);
      queryClient.invalidateQueries({ queryKey: businessProfilesKeys.list() });
    },
  });
}

export function useDeployProfile(subVerticalKey: string) {
  const queryClient = useQueryClient();
  const user = useAuth((s) => s.user);
  return useMutation({
    mutationFn: (values: DeployFormValues) =>
      deployProfile(subVerticalKey, values, {
        id: user?.id ?? 'unknown',
        name: user?.displayName ?? 'Unknown Operator',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: businessProfilesKeys.deployments(subVerticalKey),
      });
    },
  });
}
